import { useState } from 'react';

interface DonutChartProps {
  data: { label: string; value: number; color: string }[];
  title?: string;
  legendOffset?: string; // Optional class for legend positioning (e.g., "-mt-16")
  /** Strip card chrome/padding when nested inside a parent panel */
  dense?: boolean;
  /**
   * Singular unit shown in the hover tooltip (e.g. "driver", "hour").
   * Uses a small irregular map for known units; otherwise appends "s".
   */
  valueUnit?: string;
  /** Show each slice's value (with valueUnit) beside its percentage in the legend, not only on hover. */
  showValuesInLegend?: boolean;
  /** Show the value / % tooltip beside a legend item on hover (default true). */
  hoverTooltip?: boolean;
}

const VALUE_UNIT_PLURALS: Record<string, string> = {
  hour: 'hours',
  driver: 'drivers',
};

function formatValueWithUnit(value: number, unit?: string): string {
  if (!unit) return String(value);
  if (value === 1) return `${value} ${unit}`;
  const plural = VALUE_UNIT_PLURALS[unit] ?? `${unit}s`;
  return `${value} ${plural}`;
}

export default function DonutChart({ data, title, legendOffset, dense = false, valueUnit, showValuesInLegend = false, hoverTooltip = true }: DonutChartProps) {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const hasDataRows = data.length > 0;
  /** When all values are zero, legend indices do not match a single placeholder SVG slice — skip cross-highlight dimming. */
  const interactiveHover = total > 0;
  const radius = 90;
  const innerRadius = 55;
  const centerX = 110;
  const centerY = 60;
  
  // Helper function to create arc path.
  // SVG arcs collapse when start === end, so a true 360° slice draws blank.
  const createArcPath = (startAngle: number, endAngle: number, innerR: number, outerR: number) => {
    const sweep = endAngle - startAngle;
    if (sweep <= 0) return '';

    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    const x1 = centerX + innerR * Math.cos(startAngleRad);
    const y1 = centerY + innerR * Math.sin(startAngleRad);
    const x2 = centerX + outerR * Math.cos(startAngleRad);
    const y2 = centerY + outerR * Math.sin(startAngleRad);
    const x3 = centerX + outerR * Math.cos(endAngleRad);
    const y3 = centerY + outerR * Math.sin(endAngleRad);
    const x4 = centerX + innerR * Math.cos(endAngleRad);
    const y4 = centerY + innerR * Math.sin(endAngleRad);
    
    const largeArcFlag = sweep > 180 ? 1 : 0;
    
    return `M ${x1} ${y1} L ${x2} ${y2} A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${x3} ${y3} L ${x4} ${y4} A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${x1} ${y1} Z`;
  };

  /** Cap full-circle slices just under 360° so the arc path remains visible. */
  const sliceAngleDegrees = (value: number): number => {
    if (total <= 0 || value <= 0) return 0;
    const raw = (value / total) * 360;
    return raw >= 359.999 ? 359.99 : raw;
  };
  
  // When every slice is zero, avoid NaN angles and show a neutral ring; legend still lists categories at 0%.
  let currentAngle = -90;
  const segments =
    total <= 0 && hasDataRows
      ? [
          {
            label: '',
            value: 0,
            color: 'rgb(var(--accent))',
            percentage: 0,
            startAngle: -90,
            endAngle: 270,
            path: createArcPath(-90, 270, innerRadius, radius),
          },
        ]
      : data.map((item) => {
          const percentage = total > 0 ? (item.value / total) * 100 : 0;
          const angle = sliceAngleDegrees(item.value);
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          const segment = {
            ...item,
            percentage,
            startAngle,
            endAngle,
            path: createArcPath(startAngle, endAngle, innerRadius, radius),
          };
          currentAngle += angle;
          return segment;
        });

  return (
    <div className={`${dense
      ? 'bg-transparent p-0 border-0 shadow-none h-full'
      : 'bg-card rounded-lg shadow-sm p-4 border border-border'
    } w-full flex flex-col group`}>
      {title && <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-white group-hover:drop-shadow-glow-neutral-md transition-all">{title}</h3>}
      <div className={`flex items-center justify-center flex-1 relative w-full ${dense ? 'pt-0' : 'pt-4'}`}>
        <div className="flex-shrink-0 relative group/chart">
        <svg 
            width="220"
            height="220"
            viewBox="10 -40 200 200" 
            preserveAspectRatio="xMidYMid meet"
            className="transition-all duration-300 flex-shrink-0"
            onMouseLeave={() => setHoveredSegment(null)}
          >
            {segments.map((segment, index) => (
              segment.path ? (
              <path
                key={index}
                d={segment.path}
                fill={segment.color}
                className={`transition-all duration-300 ${interactiveHover ? 'cursor-pointer' : 'cursor-default'}`}
                style={{
                  opacity: !interactiveHover || hoveredSegment === null || hoveredSegment === index ? 1 : 0.5,
                }}
                onMouseEnter={() => {
                  if (interactiveHover) setHoveredSegment(index);
                }}
              />
              ) : null
            ))}
          </svg>
        </div>
        <div className={`flex flex-col gap-2 relative ml-4 ${legendOffset || ''}`}>
          {data.map((item, index) => (
            <div 
              key={index} 
              className="flex items-center gap-2 relative group/item"
              onMouseEnter={() => setHoveredSegment(index)}
              onMouseLeave={() => setHoveredSegment(null)}
            >
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0 transition-all"
                style={{ 
                  backgroundColor: item.color,
                  opacity: !interactiveHover || hoveredSegment === null || hoveredSegment === index ? 1 : 0.5,
                }}
              />
              <div>
                <div className="text-sm font-medium text-white transition-all">
                  {item.label}
                </div>
                <div className="text-sm text-muted-foreground transition-all">
                  (
                  <span className={hoveredSegment === index ? 'text-success' : ''}>
                    {interactiveHover ? ((item.value / total) * 100).toFixed(1) : '0.0'}%
                  </span>
                  {showValuesInLegend ? <>, {formatValueWithUnit(Math.round(item.value * 10) / 10, valueUnit)}</> : null}
                  )
                </div>
              </div>
              {hoverTooltip && hoveredSegment === index && total > 0 && (
                <div className="absolute left-full ml-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                  <div className="bg-background border border-border rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                    <div className="text-white text-sm font-medium">
                      {formatValueWithUnit(segments[index]?.value ?? item.value, valueUnit)}
                    </div>
                    <div className="text-white text-xs">
                      (<span className="text-success">{(segments[index]?.percentage ?? 0).toFixed(1)}%</span> of total)
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
